import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { Check } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { MenuItem, MenuList, Popover } from '../../../ds/components';
import type { DeviceOption } from '../media';
import styles from './parts.module.css';

/**
 * Building blocks of the dark room (Beratung 10–12, 14 and the guest page): video tiles with the name chip,
 * the remote audio, the device pills with their chooser, the clock pills and the round control buttons.
 */

/** Plays one `MediaStreamTrack` (camera, screen or a preview stream). */
export function TrackVideo({
  track,
  stream,
  mirrored = false,
  fit = 'cover',
  className,
}: {
  track?: MediaStreamTrack | null | undefined;
  stream?: MediaStream | null | undefined;
  mirrored?: boolean | undefined;
  fit?: 'cover' | 'contain' | undefined;
  className?: string | undefined;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const source = stream ?? (track ? new MediaStream([track]) : null);
    element.srcObject = source;
    if (source) void element.play().catch(() => undefined);
    return () => {
      element.srcObject = null;
    };
  }, [track, stream]);
  return (
    <video
      ref={ref}
      className={clsx(
        styles.video,
        mirrored && styles.mirrored,
        fit === 'contain' && styles.contain,
        className,
      )}
      autoPlay
      playsInline
      muted
      aria-hidden="true"
    />
  );
}

/** A participant tile: picture (or the placeholder), the name chip, the 2-px accent frame for the speaker. */
export function VideoTile({
  track,
  stream,
  name,
  mirrored,
  active = false,
  placeholder,
  size = 'default',
  className,
}: {
  track?: MediaStreamTrack | null | undefined;
  stream?: MediaStream | null | undefined;
  name: string;
  mirrored?: boolean | undefined;
  active?: boolean | undefined;
  placeholder?: ReactNode;
  size?: 'default' | 'small' | 'large' | undefined;
  className?: string | undefined;
}) {
  const hasPicture = Boolean(stream?.getVideoTracks().length) || Boolean(track);
  return (
    <div
      className={clsx(
        styles.tile,
        active && styles.tileActive,
        size === 'small' && styles.tileSmall,
        size === 'large' && styles.tileLarge,
        className,
      )}
    >
      {hasPicture ? (
        <TrackVideo track={track} stream={stream} mirrored={mirrored} />
      ) : (
        <div className={styles.tilePlaceholder}>{placeholder}</div>
      )}
      <span className={clsx(styles.nameChip, size === 'small' && styles.nameChipSmall)}>{name}</span>
    </div>
  );
}

/** Plays the audio tracks of the other participants (the own microphone is never played back). */
export function RemoteAudio({ tracks }: { tracks: MediaStreamTrack[] }) {
  return (
    <>
      {tracks.map((track) => (
        <AudioTrack key={track.id} track={track} />
      ))}
    </>
  );
}

function AudioTrack({ track }: { track: MediaStreamTrack }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.srcObject = new MediaStream([track]);
    void element.play().catch(() => undefined);
    return () => {
      element.srcObject = null;
    };
  }, [track]);
  return <audio ref={ref} autoPlay />;
}

/** „Kamera · FaceTime HD" — opens a menu with the devices of that kind. */
export function DevicePill({
  kind,
  label,
  devices,
  selectedId,
  onSelect,
}: {
  kind: 'Kamera' | 'Mikrofon';
  label: string | null;
  devices: DeviceOption[];
  selectedId: string | null;
  onSelect: (deviceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const text = `${kind} · ${label ?? 'nicht verfügbar'}`;
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={styles.glassPill}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={devices.length === 0}
        onClick={() => setOpen((value) => !value)}
      >
        {text}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={ref} placement="top-start" width={280}>
        <MenuList
          label={kind === 'Kamera' ? 'Kamera wählen' : 'Mikrofon wählen'}
          onClose={() => setOpen(false)}
        >
          {devices.map((device) => (
            <MenuItem
              key={device.deviceId}
              label={device.label}
              active={device.deviceId === selectedId}
              icon={device.deviceId === selectedId ? <Check size={16} strokeWidth={1.75} /> : undefined}
              onSelect={() => {
                setOpen(false);
                onSelect(device.deviceId);
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

/** „● Aufzeichnung · 24:18" (Beratung 11/12/14) — a meeting that records. */
export function RecordingPill({ children }: { children: ReactNode }) {
  return (
    <span className={styles.recordingPill}>
      <span className={styles.recordingDot} aria-hidden="true" />
      {children}
    </span>
  );
}

/** „Gespräch · 24:18" — the clock of a conversation without recording: the glass pill, no red dot (D-163). */
export function ConversationPill({ children }: { children: ReactNode }) {
  return <span className={styles.conversationPill}>{children}</span>;
}

/** Round control of the call bar (Mic, Cam, ···; 44 px desktop, 48 px mobile). */
export function RoundControl({
  label,
  pressed,
  tone = 'glass',
  size = 'default',
  onClick,
  disabled,
  ariaLabel,
  buttonRef,
  expanded,
  hasPopup,
}: {
  label: string;
  pressed?: boolean | undefined;
  tone?: 'glass' | 'accent' | 'danger' | 'off' | undefined;
  size?: 'default' | 'large' | undefined;
  onClick: () => void;
  disabled?: boolean | undefined;
  ariaLabel?: string | undefined;
  buttonRef?: Ref<HTMLButtonElement> | undefined;
  expanded?: boolean | undefined;
  hasPopup?: 'menu' | 'dialog' | undefined;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className={clsx(
        styles.round,
        size === 'large' && styles.roundLarge,
        tone === 'accent' && styles.roundAccent,
        tone === 'danger' && styles.roundDanger,
        tone === 'off' && styles.roundOff,
      )}
      aria-pressed={pressed}
      aria-label={ariaLabel}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/** Pill-shaped control („Exposé teilen", „Beenden", „Freigabe beenden"). */
export function PillControl({
  children,
  tone = 'glass',
  onClick,
  disabled,
  buttonRef,
  expanded,
  hasPopup,
  compact = false,
}: {
  children: ReactNode;
  tone?: 'glass' | 'accent' | 'danger' | undefined;
  onClick: () => void;
  disabled?: boolean | undefined;
  buttonRef?: Ref<HTMLButtonElement> | undefined;
  expanded?: boolean | undefined;
  hasPopup?: 'menu' | 'dialog' | undefined;
  compact?: boolean | undefined;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className={clsx(
        styles.pillControl,
        compact && styles.pillControlCompact,
        tone === 'accent' && styles.pillAccent,
        tone === 'danger' && styles.pillDanger,
      )}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
